import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from 'components/ui/Button';
import BreadcrumbNavigation from 'components/ui/BreadcrumbNavigation';
import TabNavigation from './components/TabNavigation';
import BusinessDetailsTab from './components/BusinessDetailsTab';
import OperatingHoursTab from './components/OperatingHoursTab';
import ContactInformationTab from './components/ContactInformationTab';
import LocationSettingsTab from './components/LocationSettingsTab';
import { useSelector } from 'react-redux';

const BusinessProfileSettings = () => {
  const navigate = useNavigate();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('business-details');
  const [isLoading, setIsLoading] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState({});
  const selectedBusiness = useSelector((state)=> state?.business.selectedBusiness);

  // Mock user data
  const user = {
    name: 'Admin User',
    email: 'admin@elitecarwash.com',
    role: 'Administrator'
  };

  // Mock business profile data
  const [businessProfile, setBusinessProfile] = useState({
    contactInformation: {
      primaryPhone: '(555) 123-4567',
      secondaryPhone: '(555) 123-4568',
      email: 'info@elitecarwash.com',
      website: 'https://www.elitecarwash.com',
      socialMedia: {
        facebook: 'https://facebook.com/elitecarwash',
        instagram: 'https://instagram.com/elitecarwash',
        twitter: 'https://twitter.com/elitecarwash',
        linkedin: ''
      },
      emergencyContact: {
        name: 'John Smith',
        phone: '(555) 987-6543',
        relationship: 'Manager'
      }
    },
    locationSettings: {
      address: {
        street: '123 Main Street',
        city: 'Los Angeles',
        state: 'CA',
        zipCode: '90210',
        country: 'United States'
      },
      coordinates: {
        latitude: '34.0522',
        longitude: '-118.2437'
      },
      serviceArea: {
        radius: '15',
        unit: 'miles'
      },
      parkingInfo: {
        hasParking: true,
        parkingSpaces: '20',
        parkingType: 'Free'
      },
      accessibility: {
        wheelchairAccessible: true,
        hasRestrooms: true,
        hasWaitingArea: true
      }
    }
  });

  // Check authentication
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      navigate('/login');
      return;
    }
  }, [navigate]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
  };

  const handleUpdate = async (section, data) => {
    setIsLoading(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setBusinessProfile(prev => ({
        ...prev,
        [section]: data
      }));
      
      setLastSaved(new Date());
      setHasUnsavedChanges(prev => ({
        ...prev,
        [activeTab]: false
      }));
      
      // Show success message (you could add a toast notification here)
      console.log(`${section} updated successfully`);
      
    } catch (error) {
      console.error('Error updating business profile:', error);
      // Handle error (you could add error notification here)
    } finally {
      setIsLoading(false);
    }
  };

  const handleSidebarToggle = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  const renderActiveTab = () => {
    switch (activeTab) {
      case 'business-details':
        return (
          <BusinessDetailsTab
            selectedBusiness={selectedBusiness}
          />
        );
      case 'operating-hours':
        return (
          <OperatingHoursTab
            selectedBusiness={selectedBusiness}
          />
        );
      case 'contact-info':
        return (
          <ContactInformationTab
            selectedBusiness={selectedBusiness}
          />
        );
      case 'location-settings':
        return (
          <LocationSettingsTab
            selectedBusiness={selectedBusiness}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <div className="p-4 lg:p-6">
        {/* Page Header */}
        <div className="mb-6">
          <BreadcrumbNavigation />
          
          <div className="flex items-center justify-between mt-4">
            <div>
              <h1 className="text-2xl font-bold text-foreground">Business Profile Settings</h1>
              <p className="text-muted-foreground mt-1">
                Manage your business information, operating hours, and location details
              </p>
            </div>
            
            <div className="flex items-center space-x-4">
              {lastSaved && (
                <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                  <div className="status-indicator active"></div>
                  <span>
                    Last saved: {lastSaved?.toLocaleTimeString()}
                  </span>
                </div>
              )}
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/admin-dashboard')}
                iconName="ArrowLeft"
                iconPosition="left"
                iconSize={16}
              >
                Back to Dashboard
              </Button>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6">
          <TabNavigation
            activeTab={activeTab}
            onTabChange={handleTabChange}
            hasUnsavedChanges={hasUnsavedChanges}
          />
        </div>

        {/* Tab Content */}
        <div className="bg-background">
          {renderActiveTab()}
        </div>

        {/* Footer Info */}
        {/* <div className="mt-8 pt-6 border-t border-border">
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Icon name="Shield" size={16} className="text-success" />
                <span>All changes are automatically backed up</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Clock" size={16} className="text-primary" />
                <span>Auto-save enabled</span>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <span>CarWash Manager</span>
              <span className="font-mono">v2.1.0</span>
            </div>
          </div>
        </div> */}
      </div>
    </>
  );
};

export default BusinessProfileSettings;